'use client';

import { useDraw } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { PropsWithChildren, ReactNode } from 'react';

type DrawLiveTransitionProps = PropsWithChildren & {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
};

/**
 * Component to handle live transition for draw events. It checks the draw status and renders children components accordingly.
 *
 * @param root0 props for DrawLiveTransition
 * @param root0.product The product associated with the draw
 * @param root0.drawNumber The draw number to check the status for
 * @param root0.children The child components to render when the draw is in the appropriate state
 * @returns A ReactNode that renders the children if the draw is in a live/finished state.
 */
export function DrawLiveTransition({ product, drawNumber, children }: DrawLiveTransitionProps): ReactNode {
  const draw = useDraw({ product, drawNumber });

  if (draw.isError) {
    return undefined;
  }

  const drawStatus = draw.data.draw.drawState.client;

  if (drawStatus === 'Opened' || drawStatus === 'Defined') {
    return undefined;
  }

  return children;
}
